import React from 'react';

import { FaCameraRetro } from '@react-icons/all-files/fa/FaCameraRetro';
import { FaInfoCircle } from '@react-icons/all-files/fa/FaInfoCircle';
import { FaSyncAlt } from '@react-icons/all-files/fa/FaSyncAlt';
import QRReader, { QRScannerProps, QRScannerRef } from 'react-qr-scanner';
import useIsMounted from '@/hooks/useIsMounted';
import chstep1 from '@/assets/img/camera_instructions/chrome_1_crop.png';
import chstep2 from '@/assets/img/camera_instructions/chrome_2_crop.png';
import chstep3 from '@/assets/img/camera_instructions/chrome_3_crop.png';
import chstep4 from '@/assets/img/camera_instructions/chrome_4_crop.png';
import chstep5 from '@/assets/img/camera_instructions/chrome_5_crop.png';
import chstep6 from '@/assets/img/camera_instructions/chrome_6_crop.png';
import ffstep1 from '@/assets/img/camera_instructions/firefox_1_crop.png';
import ssstep1 from '@/assets/img/camera_instructions/samsung_1_crop.png';
import ssstep2 from '@/assets/img/camera_instructions/samsung_2_crop.png';
import ssstep3 from '@/assets/img/camera_instructions/samsung_3_crop.png';
import ssstep4 from '@/assets/img/camera_instructions/samsung_4_crop.png';
import ssstep5 from '@/assets/img/camera_instructions/samsung_5_crop.png';
import ssstep6 from '@/assets/img/camera_instructions/samsung_6_crop.png';
import ssstep7 from '@/assets/img/camera_instructions/samsung_7_crop.png';
import ssstep8 from '@/assets/img/camera_instructions/samsung_8_crop.png';
import { useRequestMediaPermissions } from '@/hooks/useUserMedia';
import { cn } from '@/utils/utils';
import { isNullish } from '@apollo/client/cache/inmemory/helpers';
import { choosePreferredCameraDeviceId } from './qrCameraSelection';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import * as Sentry from '@sentry/browser';

export type QRReaderWithFallbackProps = {
  disabled?: boolean;
  showButton?: boolean;
} & QRScannerProps;

const nextFacingMode: Record<'front' | 'rear', 'front' | 'rear'> = {
  rear: 'front',
  front: 'rear',
};

export const QRReaderWithFallback: React.FC<QRReaderWithFallbackProps> = ({
  showButton = true,
  onScan,
  facingMode: propsFacingMode = 'rear',
  ...props
}) => {
  const isMounted = useIsMounted();
  const camera = React.useRef<QRScannerRef | null>(null);
  const [showCamera, setShowCamera] = React.useState(!showButton);
  const [disclaimerRead, setDisclaimerRead] = React.useState(false);
  const [explainerIsOpen, setExplainerIsOpen] = React.useState(false);
  const [permissionsError, setPermissionsError] = React.useState<Error>();
  const [permissionStatus, setPermissionStatus] =
    React.useState<Pick<PermissionStatus, 'state'>>();
  const [facingMode, setFacingMode] = React.useState(propsFacingMode);
  const swapFacingMode = React.useCallback(() => {
    setFacingMode((facingMode) => nextFacingMode[facingMode ?? 'front']);
  }, []);
  const [, requestPermissions] = useRequestMediaPermissions({
    video: { facingMode: 'environment' },
  });
  const disabledOnScan = React.useMemo(
    () => (props.disabled ? () => {} : onScan),
    [onScan, props.disabled]
  );
  React.useEffect(() => {
    setFacingMode(propsFacingMode);
  }, [propsFacingMode]);
  React.useEffect(() => {
    navigator?.permissions
      ?.query?.({ name: 'camera' as PermissionName })
      .then((r) => {
        if (!r) return;
        setPermissionStatus(r);
        r.onchange = function () {
          isMounted.current && setPermissionStatus(this);
        };
      })
      .catch((reason) => {
        console.error('QRReaderWithFallback permissions error', reason);
        Sentry.captureException('QRReaderWithFallback permissions error', {
          extra: { reason },
        });
        if (
          reason.message.includes(
            'is not a valid value for enumeration PermissionName'
          )
        )
          isMounted.current && setPermissionStatus({ state: 'prompt' });
        else isMounted.current && setPermissionsError(reason);
      });
    if (!navigator?.permissions?.query) {
      isMounted.current && setPermissionStatus({ state: 'prompt' });
    }
  }, [isMounted]);

  // show just a button to begin with, so it doesn't take up so much space
  if (!showCamera)
    return (
      <Button
        variant="warning"
        onClick={() => {
          setShowCamera(true);
        }}
        disabled={props.disabled}
      >
        <FaCameraRetro /> Código QR
      </Button>
    );

  // if we dont have permissions, explain situation, and offer legacy camera
  if (permissionsError) {
    return (
      <>
        <CameraPermissionsExplainer
          isOpen={explainerIsOpen}
          setIsOpen={setExplainerIsOpen}
        />
        No podemos mostrarte la cámara QR.
        <br />
        Aprendé a habilitar la cámara QR{' '}
        <Button variant="info" onClick={() => setExplainerIsOpen(true)}>
          <FaInfoCircle />
        </Button>
        <br />O usá la cámara básica usando este botón{' '}
        <Button
          variant="warning"
          onClick={() => {
            camera.current && camera.current.openImageDialog();
          }}
          disabled={false}
        >
          <FaCameraRetro />
        </Button>
        <div style={{ display: 'none' }}>
          <QRReader
            {...props}
            legacyMode={true}
            ref={camera}
            onError={(e: Error) => console.error(e)}
            onScan={(code: string) => {
              if (!code)
                toast.error(
                  'No se encontraron códigos QR en esa foto. Por favor intenta de nuevo.',
                  { duration: 3000 }
                );
              disabledOnScan(code);
            }}
          />
        </div>
      </>
    );
  }

  //when there's not an error, render camera or ask for permissions
  return (
    <>
      {!disclaimerRead && permissionStatus?.state !== 'granted' ? (
        <>
          Necesitamos tu permiso para usar la cámara QR. Por favor presioná
          Aceptar en el diálogo que veras a continuación.
          <br />
          <Button
            onClick={() => {
              setDisclaimerRead(true);
              requestPermissions();
            }}
          >
            Entendido
          </Button>
        </>
      ) : (
        <div className="QR-camera">
          <QRReader
            {...props}
            onScan={disabledOnScan}
            facingMode={facingMode}
            onError={(error: Error) => setPermissionsError(error)}
            chooseDeviceId={(matchingCameras: any[], allDevices: any[]) =>
              choosePreferredCameraDeviceId(
                matchingCameras,
                allDevices,
                facingMode
              )
            }
          />
          <div
            className={cn(
              'absolute top-4 right-4',
              'shadow-lg z-5 rounded-full mix-blend-difference'
            )}
          >
            <Button
              variant="secondary"
              size="lg-icon"
              className="rounded-full bg-background/80 text-foreground backdrop-blur-sm"
              aria-label="Cambiar entre cámara frontal y trasera"
              onClick={swapFacingMode}
            >
              <FaSyncAlt className="size-5" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

const browser = (function () {
  var test = function (regexp: RegExp) {
    return regexp.test(window.navigator.userAgent);
  };
  switch (true) {
    case test(/edg/i):
      return 'Microsoft Edge';
    case test(/trident/i):
      return 'Microsoft Internet Explorer';
    case test(/firefox|fxios/i):
      return 'Mozilla Firefox';
    case test(/opr\//i):
      return 'Opera';
    case test(/ucbrowser/i):
      return 'UC Browser';
    case test(/samsungbrowser/i):
      return 'Samsung Browser';
    case test(/chrome|chromium|crios/i):
      return 'Google Chrome';
    case test(/safari/i):
      return 'Apple Safari';
    default:
      return 'Otros';
  }
})();

export const CameraPermissionsExplainer: React.FC<{
  isOpen: boolean;
  setIsOpen: (s: ((s: boolean) => boolean) | boolean) => void;
}> = (props) => {
  return (
    <Dialog open={props.isOpen} onOpenChange={() => props.setIsOpen((o) => !o)}>
      <DialogContent className="max-h-[70dvh]! grid grid-rows-[auto_1fr]">
        <DialogHeader>
          <DialogTitle>Información</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto grid gap-4 [&_li]:list-disc [&_li]:list-inside [&_ol]:leading-loose">
          Asi se habilita la cámara en {browser}:
          {browser === 'Google Chrome' ? (
            <ChromeInstructions />
          ) : browser === 'Mozilla Firefox' ? (
            <FirefoxInstructions />
          ) : browser === 'Samsung Browser' ? (
            <SamsungInstructions />
          ) : (
            <>
              <br />
              Lo sentimos, las instrucciones para tu navegador aún no están
              listas. Comunicate con{' '}
              <a href="mailto:info@bilderit.com">info@bilderit.com</a>
              para que podamos ayudarte.
            </>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => props.setIsOpen(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const url = window.location.protocol + '//' + window.location.host + '/';

const ChromeInstructions = () => (
  <ol>
    {window.matchMedia('(display-mode: standalone)').matches && (
      <li>
        Abrí Google Chrome y entrá a <a href={url}>{url}</a>
      </li>
    )}
    <li>Presioná los tres puntitos arriba a la derecha</li>
    <img
      src={chstep1}
      alt="Presioná los tres puntitos arriba a la derecha"
      style={{ width: '100%' }}
    />
    <li>
      Presioná el ícono <FaInfoCircle />
    </li>
    <img src={chstep2} alt="Presioná el ícono i" style={{ width: '100%' }} />
    <li>Presioná Configuración de sitios</li>
    <img
      src={chstep3}
      alt="Presioná Configuración de sitios"
      style={{ width: '100%' }}
    />
    <li>Presioná Acceso a la cámara</li>
    <img
      src={chstep4}
      alt="Presioná Acceso a la cámara"
      style={{ width: '100%' }}
    />
    <li>Presioná Permitir</li>
    <img src={chstep5} alt="Presioná Permitir" style={{ width: '100%' }} />
    <li>Presioná Atrás</li>
    <img src={chstep6} alt="Presioná Atrás" style={{ width: '100%' }} />
  </ol>
);

const FirefoxInstructions = () => (
  <ol>
    <li>Presioná "Compartir" cuando Bilderit pida usar la cámara</li>
    <img
      src={ffstep1}
      alt='Presioná "Compartir" cuando Bilderit pida usar la cámara'
      style={{ width: '100%' }}
    />
  </ol>
);

const SamsungInstructions = () => (
  <ol>
    {/* Samsung internet no permite detectar si la app esta instalada... por las dudas siempre pido q abran el browser */}
    <li>
      Abrí Samsung Internet y entrá a <a href={url}>{url}</a>
    </li>
    <li>Presioná las tres rayitas abajo a la derecha</li>
    <img
      src={ssstep1}
      alt="Presioná las tres rayitas abajo a la derecha"
      style={{ width: '100%' }}
    />
    <li>Presioná "Ajustes"</li>
    <img src={ssstep2} alt='Presioná "Ajustes"' style={{ width: '100%' }} />
    <li>Sitios web y descargas</li>
    <img src={ssstep3} alt="Sitios web y descargas" style={{ width: '100%' }} />
    <li>Administrar datos</li>
    <img src={ssstep4} alt="Administrar datos" style={{ width: '100%' }} />
    <li>Presioná "Eliminar"</li>
    <img src={ssstep5} alt='Presioná "Eliminar"' style={{ width: '100%' }} />
    <li>Seleccioná todas las filas que digan bilderit.com</li>
    <img
      src={ssstep6}
      alt="Seleccioná todas las filas que digan bilderit.com"
      style={{ width: '100%' }}
    />
    <li>Presioná "Eliminar"</li>
    <img src={ssstep7} alt='Presioná "Eliminar"' style={{ width: '100%' }} />
    <li>Presioná Atras hasta volver a Bilderit</li>
    <img src={ssstep8} alt="Presioná Atras" style={{ width: '100%' }} />
  </ol>
);

export const parseQR = (code: string) => {
  let result: { machine_id: string | number | null; error: string | null } = {
    machine_id: null,
    error: null,
  };
  const query = code.split('?')[1];
  if (!query) {
    result.error = 'Invalid QR code';
  } else {
    const urlParams = new URLSearchParams(query);
    const machineId = urlParams.get('machine_id');
    result.machine_id = isNullish(machineId)
      ? null
      : isNaN(Number(machineId))
        ? machineId
        : Number(machineId);
  }
  return result;
};
